import React, { useEffect, useState } from "react";
import { FiActivity, FiArrowRight, FiBox, FiDroplet, FiSun, FiThermometer, FiWind, FiZap } from "react-icons/fi";

import ProfilePictureRounded from "../ProfilePictureRounded";

import { Skeleton, SkeletonText } from "../Skeleton";
import { imageCache } from "../../utils/imageCache";
import { ModelGardenItem } from "../Modal/ModelGardenModal/types";

// Categories are free text, so keywords pick the tile; each family gets its own existing tint so they read apart at a glance
const CATEGORY_STYLES: { match: RegExp; tile: string; Icon: React.ComponentType<{ className?: string }> }[] = [
  { match: /hydro|water|flow|river|rain|precip|snow/, tile: "bg-eai-brand-cyan/60 text-eai-teal-deep", Icon: FiDroplet },
  { match: /wind/, tile: "bg-eai-slash/15 text-eai-neutral", Icon: FiWind },
  { match: /solar|sun|irradi/, tile: "bg-eai-brand-peach text-eai-warning-text", Icon: FiSun },
  { match: /energy|power|load|grid|generat|demand/, tile: "bg-eai-cta-primary/15 text-eai-cta-primary-hover", Icon: FiZap },
  { match: /weather|temp|climate|heat/, tile: "bg-eai-brand-peach text-eai-warning-text", Icon: FiThermometer },
  { match: /forecast|statist|time.?series|ml|machine/, tile: "bg-eai-surface-dark/10 text-eai-surface-dark", Icon: FiActivity },
];

const FALLBACK_STYLE = { tile: "bg-eai-surface-dark/10 text-eai-surface-dark", Icon: FiBox };

const categoryStyle = (category?: string) =>
  CATEGORY_STYLES.find(({ match }) => match.test((category || "").toLowerCase())) || FALLBACK_STYLE;

interface TemplateCardProps {
  template: ModelGardenItem;
  onSelect: (template: ModelGardenItem) => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({ template, onSelect }) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const { tile, Icon } = categoryStyle(template.category);
  const meta = [template.category, template.version && `v${template.version}`].filter(Boolean).join(" · ");

  useEffect(() => {
    let cancelled = false;
    if (template.thumbnail) {
      imageCache
        .getSignedUrl(template.thumbnail)
        .then((url: string | null) => {
          if (!cancelled) setThumbnailUrl(url);
        })
        .catch(() => {
          if (!cancelled) setThumbnailUrl(null);
        });
    }
    // Guard against a late resolve landing after the card unmounts or the template changes
    return () => {
      cancelled = true;
    };
  }, [template.thumbnail]);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(template)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(template);
        }
      }}
      className="eai-card-interactive group flex h-full w-full min-w-0 cursor-pointer flex-col gap-3 p-4 outline-none focus-visible:ring-2 focus-visible:ring-eai-cta-primary focus-visible:ring-offset-2"
    >
      <div className="flex items-start gap-3">
        {/* A thumbnail takes the tile when the template has one; otherwise the category icon on its tint */}
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-eai-card ${thumbnailUrl ? "" : tile}`}>
          {thumbnailUrl ? (
            <img src={thumbnailUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <Icon className="h-5 w-5" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="eai-card-title line-clamp-2 min-h-[2.4em] break-words" title={template.name}>
            {template.name}
          </h3>
          {meta && (
            <p className="mt-0.5 truncate font-inter text-eai-body-xs text-eai-ink-subtle">{meta}</p>
          )}
        </div>
      </div>

      {/* Clamped to two lines so a long description can't stretch the row; the full text is the tooltip */}
      <p className="eai-card-description" title={template.description}>
        {template.description}
      </p>

      {/* Category already sits under the title, so the pills are the kind, Community and the first three tags */}
      {(template.modelKind === "HISTORICAL" || template.type === "Community" || template.tags?.length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {template.modelKind === "HISTORICAL" && (
            <span className="eai-tag-pill-sm border-eai-teal-deep/40 text-eai-teal-deep" title="Processes a dataset's history as it arrives" data-testid="historical-pill">
              HISTORICAL
            </span>
          )}
          {template.type === "Community" && (
            <span className="eai-tag-pill-sm border-eai-hero-accent text-eai-teal-deep">Community</span>
          )}
          {template.tags?.slice(0, 3).map((tag) => (
            <span key={tag} className="eai-tag-pill-sm">
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="mt-auto flex items-center gap-2 border-t border-eai-border pt-2.5">
        <ProfilePictureRounded
          pictureURL={template.creator.profilePictureUrl}
          diameter={22}
          className=""
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        />
        <span className="min-w-0 flex-1 truncate font-inter text-eai-body-xs font-medium text-eai-ink-subtle">
          {template.creator.name}
        </span>
        <FiArrowRight aria-hidden className="h-4 w-4 shrink-0 text-eai-ink-muted transition-transform duration-200 group-hover:translate-x-0.5" />
      </div>
    </div>
  );
};

// Placeholder matching TemplateCard's layout — keep the two in sync when the card changes.
export const TemplateCardSkeleton: React.FC = () => (
  <div className="eai-card flex h-full w-full min-w-0 flex-col gap-3 p-4">
    <div className="flex items-start gap-3">
      <Skeleton className="h-10 w-10 shrink-0" radius="md" />
      <div className="flex-1">
        <Skeleton className="h-3.5 w-2/3" />
        <Skeleton className="mt-2 h-3 w-1/3" />
      </div>
    </div>
    <div className="min-h-[2.9em]">
      <SkeletonText lines={2} lineClassName="h-3" />
    </div>
    <div className="flex gap-1.5">
      <Skeleton className="h-6 w-16" radius="full" />
      <Skeleton className="h-6 w-12" radius="full" />
    </div>
    <div className="mt-auto flex items-center gap-2 border-t border-eai-border pt-2.5">
      <Skeleton className="h-[22px] w-[22px]" radius="full" />
      <Skeleton className="h-3 w-24" />
    </div>
  </div>
);

export default TemplateCard;
